//@ts-check

/**
 * Renders the Brand Partner license opt-OUT checkbox inside the Billing Address
 * block.
 *
 * The included Brand Partner license is issued by default whenever the cart
 * contains a Branding licensing medium AND the user picked "fonts will be used
 * by a client". This checkbox lets the buyer opt out:
 *
 *   "I do not need the fonts. Do not issue the Brand Partner license to me."
 *
 * Submission (paired-field trick so the default is "issue"):
 * - A hidden input `order[should_issue_partner_license]=1` always submits.
 * - The checkbox submits `order[should_issue_partner_license]=0` only when
 *   checked. Because it follows the hidden input, Rails keeps the last value,
 *   so checked => 0 (opt out), unchecked => 1 (issue).
 *
 * The public API is expressed in terms of `shouldIssue` (true = license will be
 * issued); the inverted checkbox state is an internal detail.
 */
export class PartnerLicenseToggleComponent {
  constructor({ shouldIssue, onChange, label }) {
    this.initialShouldIssue = shouldIssue;
    this.onChange = onChange;
    // Label text is owned by SiteText ("licensing.branding.partner_license.opt_out")
    // and passed in from a server-rendered data attribute.
    this.label =
      label ||
      "I do not need the fonts. Do not issue the Brand Partner license to me.";
  }

  appendTo(parentElement) {
    this.element = document.createElement("div");
    this.element.dataset.mergeToggle = "partner-license";

    this.element.innerHTML = `
      <input type="hidden" name="order[should_issue_partner_license]" value="1">
      <input
        type="checkbox"
        id="order_should_issue_partner_license"
        name="order[should_issue_partner_license]"
        value="0"
      >
      <label for="order_should_issue_partner_license" data-id="should_issue_partner_license_toggle"></label>
    `;
    this.element.querySelector("label").textContent = this.label;
    parentElement.appendChild(this.element);

    this.checkbox = /** @type {HTMLInputElement} */ (
      this.element.querySelector('input[type="checkbox"]')
    );
    // Checked = opted out = NOT issuing.
    this.checkbox.checked = !this.initialShouldIssue;

    this.addListeners();
  }

  // `value` is the desired "should issue" state.
  setShouldIssue(value) {
    if (this.checkbox) this.checkbox.checked = !value;
  }

  get shouldIssue() {
    return this.checkbox ? !this.checkbox.checked : true;
  }

  setVisible(visible) {
    if (!this.element) return;
    this.element.classList.toggle("hidden", !visible);
  }

  addListeners() {
    this._handleChange = () => this.onChange(this.shouldIssue);
    this.checkbox.addEventListener("change", this._handleChange);
  }

  removeListeners() {
    this.checkbox?.removeEventListener("change", this._handleChange);
  }
}
